/**
 * Image element renderer
 * Extracted from Canvas.jsx to reduce file size
 */
import React from "react";
import { Image as KonvaImage } from "react-konva";
import ImageElement from "../../image/ImageElement";
import { isElementInViewport } from "../../../utils/viewportCullingUtils";
import { isPrimaryMouseButton } from "../../../utils/pointerButtonUtils";
import { getOffCanvasMainColumnBounds } from "../../../utils/boundaryUtils";

// Reduced from 80ms to 16ms for better cross-canvas drag performance
const DRAG_STATE_THROTTLE_MS = 16;
let _lastImageDragStateTime = 0;
const DEBUG_OCCLUSION = true;

/**
 * Creates an image element renderer factory function
 */
export const createImageRenderer = ({
  images,
  offCanvasImages,
  setImages,
  setOffCanvasImages,
  selectedObject,
  effectiveZoomLevel,
  disableInteraction,
  debouncedStateUpdate,
  isTransformingRef,
  updateImageProperties,
  setSelectedObject,
  setSelectedObjects,
  selectedObjects = [],
  setSelectedImage, // Direct setter for selectedImage (used by ImageEditor)
  setShowTextBox,
  setSelectedText,
  setSelectedArrow,
  setSelectedDoubleSidedArrow,
  setSelectedLine,
  onClick,
  handleElementDragEnd,
  saveToHistory,
  handleDragPreview,
  clearDragPreview,
  handleElementCopy,
  lockedAction,
  isCloneMode = false, // Clone mode flag - prevents selection of newly created clones
  cloneTemplate = null,
  cloneTemplates = [],
  isElementBeingEdited = false,
  setPendingChanges = () => {},
  pendingChangesRef = null, // Ref to update synchronously
  latestUpdatedImagesRef = null, // Ref to store latest updated images from transform operations
  isMultiSelectMode = false, // Multi-select mode flag
  handleGroupDragStart = null, // Handler for group drag start
  handleGroupDragMove = null, // Handler for group drag move
  cancelActiveGroupDrag = null,
  stageRef = null, // Main canvas stage ref (for coordinate conversion)
  offCanvasStageRef = null, // Off-canvas stage ref (for coordinate conversion)
  topLayerStageRef = null, // Top-layer stage ref (for top-layer image source detection)
  isGroupDraggingRef = null, // Ref to track if we're in a group drag
  recentDraggableElementMousedownRef = null, // Ref for two-click selection behavior
  justFinishedTransformRef = null, // Ref to skip clearing selection on next stage click after transform end
  lastTransformStartTimeRef = null, // Time-based fallback for transform-end click skip
  justFinishedDragRef = null, // Ref to skip clearing selection on click that fires after drag end (e.g. transform then drag)
  lastDraggedImageRef = null, // { id, time } so any clear-selection path can skip if selected image was just dragged
  selectionBoxRef = null,
  selectionBoxStageRef = null,
  // Element arrays for cross-canvas selection check
  rectangles = [],
  offCanvasRectangles = [],
  circles = [],
  offCanvasCircles = [],
  texts = [],
  offCanvasTexts = [],
  arrows = [],
  offCanvasArrows = [],
  lines = [],
  offCanvasLines = [],
  doubleSidedArrows = [],
  offCanvasDoubleSidedArrows = [],
  blockClicksOnOffCanvasElements = false,
  blockClicksOnMainStageElements = false,
  crossStageGestureBegin = null,
  crossStageGestureEnd = null,
}) => {
  const isMainNonTopTransformDebugEnabled = (() => {
    try {
      return (
        typeof window !== "undefined" &&
        window.localStorage?.getItem("debugMainNonTopImageTransform") === "1"
      );
    } catch {
      return false;
    }
  })();
  const isDuplicatePlacementActive =
    isCloneMode || !!lockedAction || !!cloneTemplate || cloneTemplates.length > 0;
  // Plain object to track last position update time for throttling (not a React ref)
  const lastPositionUpdate = { timestamp: 0, x: null, y: null };

  const getStageForCulling = (isOffCanvas) => {
    const stage =
      (isOffCanvas ? offCanvasStageRef?.current : stageRef?.current) ||
      topLayerStageRef?.current;
    return stage;
  };

  return ({
    imageObj,
    isOffCanvas = false,
    sourceStageRef,
    isPreview = false,
    opacity = undefined, // Opacity override for dimming non-selected elements
  }) => {
    // Helper function to check if an element is in main canvas (must match Canvas isElementInMainCanvasArray)
    // Arrow/DoubleSidedArrow can be in lines array (unified line/arrow elements)
    const isElementInMainCanvas = (elementId, elementType) => {
      if (!elementId || !elementType) return false;
      switch (elementType) {
        case "Rect":
          return rectangles.some((r) => r.id === elementId);
        case "Circle":
          return circles.some((c) => c.id === elementId);
        case "Image":
          return images.some((i) => i.id === elementId);
        case "Text":
          return texts.some((t) => t.id === elementId);
        case "Arrow":
          return (
            arrows.some((a) => a.id === elementId) ||
            lines.some((l) => {
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return (
                l.id === elementId &&
                (hasStartArrow || hasEndArrow) &&
                !(hasStartArrow && hasEndArrow)
              );
            })
          );
        case "Line":
          return lines.some((l) => l.id === elementId);
        case "DoubleSidedArrow":
          return (
            doubleSidedArrows.some((d) => d.id === elementId) ||
            lines.some((l) => {
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return l.id === elementId && hasStartArrow && hasEndArrow;
            })
          );
        default:
          return false;
      }
    };

    // Use provided opacity or element's own opacity, defaulting to 1
    const elementOpacity =
      opacity !== undefined
        ? opacity
        : imageObj.opacity !== undefined
          ? imageObj.opacity
          : 1;
    const setImageState = isOffCanvas ? setOffCanvasImages : setImages;
    const isInteractive = !isPreview;
    // CRITICAL: Hide transformer when multiple objects are selected - show selection box instead
    // Also hide transformer if this element is part of selectedObjects array (multi-select mode)
    const isInSelectedObjects =
      selectedObjects &&
      selectedObjects.some(
        (obj) => obj.id === imageObj.id && obj.type === "Image",
      );

    const isSelected =
      isInteractive &&
      imageObj.id === selectedObject?.id &&
      selectedObject?.type === "Image" &&
      (!selectedObjects || selectedObjects.length <= 1); // Only show transformer for single selection

    // When duplicate-to-selected-location is on, no drag (single or group). Otherwise draggable if selected or in multi-selection.
    const isDraggableElement =
      !isCloneMode &&
      !lockedAction &&
      (isSelected || (isInSelectedObjects && selectedObjects.length > 1));

    // DEBUG: Log draggable state for rotated images
    const elementRotation = imageObj.rotation || 0;
    if (elementRotation !== 0) {
    }

    // CRITICAL FIX: Track transform state for all images
    // For main canvas top-layer images: transformer is in Canvas.jsx
    // For off-canvas top-layer images: transformer is local in ImageElement
    // For non-top-layer images: transformer is local in ImageElement
    const isCurrentlyTransforming =
      isTransformingRef?.current?.isTransforming === true;

    // Only disable interactions for main canvas top-layer images during transform
    // (their transformer is in Canvas.jsx, so we need to prevent interference)
    const shouldDisableInteractions =
      isCurrentlyTransforming && imageObj.isOnTopLayer && !isOffCanvas;

    // Pass isTransforming to ImageElement for ALL images so they know not to fix position during transform
    const isImageTransforming =
      isCurrentlyTransforming &&
      // Main canvas top-layer images: check if this specific image is being transformed
      ((imageObj.isOnTopLayer &&
        !isOffCanvas &&
        imageObj.id === selectedObject?.id) ||
        // Off-canvas top-layer images: check if this specific image is being transformed
        (imageObj.isOnTopLayer &&
          isOffCanvas &&
          imageObj.id === selectedObject?.id) ||
        // Non-top-layer images: check if this specific image is being transformed
        (!imageObj.isOnTopLayer && imageObj.id === selectedObject?.id));

    // DEBUG: Log transform state for rotated images
    const imageRotation = imageObj.rotation || 0;
    if (imageRotation !== 0) {
    }

    // CRITICAL: Resolve image from state so we always pass the latest image data.
    // Use fromArray as base so position (x, y) stays in sync with drag updates from images state.
    // Only override image/src from selection when selection is the source of truth:
    // - _replacedAt set (explicit "just updated" e.g. after replace), or
    // - selection.src === baseImg.src (selection in sync with array).
    // When selection has different src than array and no _replacedAt, array is newer (e.g. after
    // crop we don't update selection), so use baseImg so the new cropped image shows immediately.
    const sourceArray = isOffCanvas ? offCanvasImages : images;
    const fromArray =
      sourceArray && imageObj?.id
        ? sourceArray.find((i) => i.id === imageObj.id)
        : null;
    const baseImg = fromArray || imageObj;
    const isSelectedImage =
      selectedObject?.type === "Image" && selectedObject?.id === imageObj.id;
    const fromSelection = isSelectedImage
      ? selectedObject
      : selectedObjects?.find(
          (o) => o.id === imageObj.id && o.type === "Image",
        ) || null;
    const selectionInSyncOrJustReplaced =
      fromSelection &&
      (fromSelection._replacedAt ||
        (fromSelection.src && baseImg?.src === fromSelection.src));
    const imgToPass =
      selectionInSyncOrJustReplaced && fromSelection
        ? {
            ...baseImg,
            image: fromSelection.image,
            src: fromSelection.src,
            _replacedAt: fromSelection._replacedAt,
          }
        : baseImg;
    // Keep crop/straighten backing fields stable across selection/unselection transitions.
    // During some transitions selectedObject carries richer image internals than array state (or vice versa).
    // If these fields temporarily disappear, ImageElement can recompute/reset crop rotation state and blink.
    const normalizeImageForRender = (primary, fallback) => {
      if (!primary) return fallback || primary;
      const pick = (key) =>
        primary[key] !== undefined && primary[key] !== null
          ? primary[key]
          : fallback?.[key];
      return {
        ...primary,
        image: pick("image"),
        originalImage: pick("originalImage"),
        originalImageDataUrl: pick("originalImageDataUrl"),
        imageDataUrl: pick("imageDataUrl"),
        originalWidth: pick("originalWidth"),
        originalHeight: pick("originalHeight"),
        cropData: pick("cropData"),
        originalCropRotation: pick("originalCropRotation"),
        hasBakedTransforms: pick("hasBakedTransforms"),
        flipHorizontal: pick("flipHorizontal"),
        flipVertical: pick("flipVertical"),
      };
    };
    const stableImgToPass = normalizeImageForRender(imgToPass, imageObj);
    // Preserve transient render-only flags from caller-provided image object.
    // Canvas may pass temporary flags (e.g. _forceOcclusionBlocker) that are not in state arrays.
    const imgToPassWithRenderFlags = {
      ...stableImgToPass,
      // Preserve render-time top-layer override from caller (Canvas conversion during selection).
      ...(imageObj &&
      Object.prototype.hasOwnProperty.call(imageObj, "isOnTopLayer")
        ? { isOnTopLayer: imageObj.isOnTopLayer }
        : {}),
      // Preserve transient blocker flag from caller.
      ...(imageObj && imageObj._forceOcclusionBlocker === true
        ? { _forceOcclusionBlocker: true }
        : {}),
    };
    
    // VIEWPORT CULLING:
    // If this image is far outside the current stage viewport, and it's not selected or part of a multi-selection,
    // skip rendering it to keep the canvas responsive when there are many images.
    const shouldAlwaysRender =
      isPreview ||
      isSelected ||
      isInSelectedObjects ||
      imageObj.isOnTopLayer ||
      imageObj._forceOcclusionBlocker === true ||
      isImageTransforming ||
      isGroupDraggingRef?.current;

    if (!shouldAlwaysRender) {
      const stage = getStageForCulling(isOffCanvas);
      if (!isElementInViewport(imgToPass, "Image", stage)) {
        return null;
      }
    }

    // PERFORMANCE: Draw-to-create preview should be lightweight.
    // `ImageElement` has many effects/memos (crop/straighten/replace handling) that are great for
    // finalized images but can be expensive when x/y/width/height change every pointer move.
    if (imageObj?.isNew === true) {
      const useTopLeftAnchor = imgToPassWithRenderFlags?.useTopLeftAnchor === true;
      const w = imgToPassWithRenderFlags?.width || 0;
      const h = imgToPassWithRenderFlags?.height || 0;
      return (
        <KonvaImage
          key={`${isPreview ? "preview" : "live"}-${isOffCanvas ? "off" : "main"}-new-${imageObj.id}`}
          image={imgToPassWithRenderFlags?.image || null}
          x={imgToPassWithRenderFlags?.x || 0}
          y={imgToPassWithRenderFlags?.y || 0}
          width={w}
          height={h}
          opacity={elementOpacity}
          rotation={imgToPassWithRenderFlags?.rotation || 0}
          scaleX={imgToPassWithRenderFlags?.scaleX ?? 1}
          scaleY={imgToPassWithRenderFlags?.scaleY ?? 1}
          offsetX={useTopLeftAnchor ? 0 : w / 2}
          offsetY={useTopLeftAnchor ? 0 : h / 2}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
    }

    return (
      <ImageElement
        // IMPORTANT: Keep key stable across transient render-only flags (e.g. occlusion demotion).
        // Canvas may clone top-layer images with `isOnTopLayer:false` + `_forceOcclusionBlocker:true`
        // when a non-top-layer element is selected. If those flags are part of the key, React will
        // remount ImageElement which is visible as "blinking".
        //
        // Only change key when the actual underlying bitmap/source is replaced.
        key={`${isPreview ? "preview" : "live"}-${isOffCanvas ? "off" : "main"}-${imageObj.id}-${
          imageObj?._replacedAt || ""
        }`}
        img={{ ...imgToPassWithRenderFlags, opacity: elementOpacity }}
        isDraggable={
          isInteractive &&
          !disableInteraction &&
          isDraggableElement &&
          !shouldDisableInteractions
        }
        isTransforming={isImageTransforming}
        isMultiSelectMode={isMultiSelectMode}
        selectedObjectsCount={selectedObjects?.length || 0}
        constrainToStage={true}
        setImages={isOffCanvas ? setOffCanvasImages : setImages}
        effectiveZoomLevel={effectiveZoomLevel}
        isGroupDraggingRef={isGroupDraggingRef} // Pass group drag ref to ImageElement
        mainCanvasStageRef={isOffCanvas ? stageRef : null}
        crossStageGestureBegin={crossStageGestureBegin}
        crossStageGestureEnd={crossStageGestureEnd}
        onDragStart={
          isInteractive && isDraggableElement
            ? () => {
                // CRITICAL FIX: Initialize group drag immediately if element is part of multi-selection
                // This prevents individual movement before group drag takes over
                const isInSelectedObjects = selectedObjects?.some(
                  (obj) => obj.id === imageObj.id && obj.type === "Image",
                );

                if (
                  selectedObjects &&
                  selectedObjects.length > 1 &&
                  isInSelectedObjects
                ) {
                  // Initialize group drag immediately when drag starts
                  handleGroupDragStart?.(imageObj.id, "Image");
                  return; // Don't proceed with individual drag logic
                }

                // NOTE: For individual drag, handleGroupDragStart will be called in onDragMove (below)
                // We don't call it here to avoid initializing group drag on a simple click
                // Individual drag handling for top-layer images is handled in onDragMove
              }
            : undefined
        }
        onClick={
          isInteractive
            ? (e) => {
                if (!isPrimaryMouseButton(e.evt)) {
                  return;
                }
                // CRITICAL: Handle false clicks on top-layer images
                // When a click is detected outside the actual image bounds, ImageElement passes a synthetic stage event
                if (
                  e._isFalseImageClick &&
                  e.target === e.target.getStage?.()
                ) {
                  // For top-layer images, false-hit detection can occur while interacting
                  // with transformer anchors/overlay timing. Do not clear selection here.
                  // Clearing causes anchors to disappear and click to fall through to stage.
                  if (imageObj.isOnTopLayer) {
                    e.evt?.preventDefault?.();
                    e.evt?.stopPropagation?.();
                    e.cancelBubble = true;
                    return;
                  }

                  // Clear selection
                  setSelectedObject(null);
                  setSelectedObjects([]);
                  setShowTextBox(false);
                  setSelectedText(null);
                  setSelectedArrow(null);
                  setSelectedDoubleSidedArrow(null);
                  setSelectedLine(null);

                  return;
                }

                // CRITICAL: For top-layer images, verify click is actually on the image
                // This prevents false clicks on rotated images where the bounding box extends beyond the visible image
                if (imageObj.isOnTopLayer) {
                  const stage = e.target?.getStage?.();
                  if (stage) {
                    const pointerPos = stage.getPointerPosition();
                    if (pointerPos) {
                      const clickedNode = stage.getIntersection(pointerPos);
                      const isActuallyOnImage =
                        clickedNode && clickedNode.id() === imageObj.id;

                      if (!isActuallyOnImage) {
                        // Click is not on image, ignore it
                        e.evt?.stopPropagation();
                        e.evt?.preventDefault();
                        return;
                      }
                    }
                  }
                }

                // Disable selection if a tool is locked (clone mode)
                if (lockedAction || isCloneMode) {
                  e.evt?.preventDefault?.();
                  e.evt?.stopPropagation?.();
                  return;
                }

                // When multi-select is on and selection is from the other stage, prevent clicking on this stage's elements
                if (isOffCanvas && blockClicksOnOffCanvasElements) {
                  e.evt?.preventDefault?.();
                  e.evt?.stopPropagation?.();
                  return;
                }
                if (!isOffCanvas && blockClicksOnMainStageElements) {
                  e.evt?.preventDefault?.();
                  e.evt?.stopPropagation?.();
                  return;
                }

                const isSameElement =
                  selectedObject && selectedObject.id === imageObj.id;

                // Cross-canvas selection check: only allow adding elements from the same stage in multi-select
                // CRITICAL: Always include both selectedObjects AND selectedObject so single selection
                // (e.g. one arrow only in selectedObject) is considered — otherwise we'd allow adding off-canvas image
                const allSelectedElements = [];
                const seenIds = new Set();
                if (selectedObjects && selectedObjects.length > 0) {
                  selectedObjects.forEach((obj) => {
                    if (obj && obj.id && obj.type && !seenIds.has(obj.id)) {
                      seenIds.add(obj.id);
                      allSelectedElements.push(obj);
                    }
                  });
                }
                if (
                  selectedObject &&
                  selectedObject.id &&
                  selectedObject.type &&
                  !seenIds.has(selectedObject.id)
                ) {
                  seenIds.add(selectedObject.id);
                  allSelectedElements.push(selectedObject);
                }

                if (allSelectedElements.length > 0) {
                  // Check if all selected elements are from the same canvas type
                  const selectedElementsCanvasTypes = allSelectedElements.map(
                    (obj) => isElementInMainCanvas(obj.id, obj.type),
                  );
                  const allFromMainCanvas = selectedElementsCanvasTypes.every(
                    (isMain) => isMain === true,
                  );
                  const allFromOffCanvas = selectedElementsCanvasTypes.every(
                    (isMain) => isMain === false,
                  );

                  // Check if clicked image is from a different canvas
                  const clickedImageIsMainCanvas = isElementInMainCanvas(
                    imageObj.id,
                    "Image",
                  );

                  if (
                    (allFromMainCanvas && !clickedImageIsMainCanvas) ||
                    (allFromOffCanvas && clickedImageIsMainCanvas)
                  ) {
                    // In multi-select: do not add element from other stage — block and return
                    if (isMultiSelectMode && isOffCanvas && allFromMainCanvas) {
                      e.evt?.preventDefault?.();
                      e.evt?.stopPropagation?.();
                      return;
                    }
                    if (isMultiSelectMode && !isOffCanvas && allFromOffCanvas) {
                      e.evt?.preventDefault?.();
                      e.evt?.stopPropagation?.();
                      return;
                    }

                    // Normal mode cross-canvas behavior:
                    // first click clears current selection only.
                    if (!isMultiSelectMode) {
                      setSelectedObject(null);
                      setSelectedObjects([]);
                      setShowTextBox(false);
                      setSelectedText(null);
                      if (setSelectedArrow) {
                        setSelectedArrow(null);
                      }
                      if (setSelectedDoubleSidedArrow) {
                        setSelectedDoubleSidedArrow(null);
                      }
                      if (setSelectedLine) {
                        setSelectedLine(null);
                      }
                      e.evt?.preventDefault?.();
                      e.evt?.stopPropagation?.();
                      e.cancelBubble = true;
                      return;
                    }
                  }
                }

                const latestImage =
                  (isOffCanvas ? offCanvasImages : images).find(
                    (img) => img.id === imageObj.id,
                  ) || imageObj;

                const newSelected = { ...latestImage, type: "Image" };
                const selectedObjectIsMainCanvas =
                  selectedObject?.id && selectedObject?.type
                    ? isElementInMainCanvas(selectedObject.id, selectedObject.type)
                    : null;
                const isSwitchingBetweenOffCanvasElements =
                  !isMultiSelectMode &&
                  !!selectedObject?.id &&
                  selectedObject.id !== imageObj.id &&
                  selectedObjectIsMainCanvas === false &&
                  isOffCanvas === true;
                if (isSwitchingBetweenOffCanvasElements) {
                  setSelectedObject(null);
                  setSelectedObjects([]);
                  setShowTextBox(false);
                  setSelectedText(null);
                  if (setSelectedArrow) setSelectedArrow(null);
                  if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
                  if (setSelectedLine) setSelectedLine(null);
                  setSelectedImage(null);
                  e.evt?.preventDefault?.();
                  e.evt?.stopPropagation?.();
                  e.cancelBubble = true;
                  return;
                }

                // CRITICAL FIX: In multi-select mode, top-layer image clicks must be handled HERE.
                // The canvas click handler uses stageRef (main stage) and may not run or may clear;
                // doing the merge locally keeps rect+circle+top-layer image selection intact.
                if (isMultiSelectMode) {
                  const isAlreadySelectedInGroup = (selectedObjects || []).some(
                    (obj) => obj.id === imageObj.id && obj.type === "Image",
                  );
                  if (isAlreadySelectedInGroup) {
                    cancelActiveGroupDrag?.();
                    const updated = (selectedObjects || []).filter(
                      (obj) => !(obj.id === imageObj.id && obj.type === "Image"),
                    );
                    const nextPrimary = updated[0] || null;
                    setSelectedObjects(updated);
                    setSelectedObject(nextPrimary);
                    setSelectedImage(
                      nextPrimary?.type === "Image" ? nextPrimary : null,
                    );
                    setSelectedText(null);
                    setShowTextBox(false);
                    setSelectedArrow(null);
                    setSelectedDoubleSidedArrow(null);
                    setSelectedLine(null);
                    let n = e?.target;
                    while (n) {
                      if (typeof n.stopDrag === "function") {
                        try {
                          n.stopDrag();
                        } catch (_) {
                          /* ignore */
                        }
                        break;
                      }
                      n = n.getParent?.() ?? null;
                    }
                    e.evt?.preventDefault?.();
                    e.evt?.stopPropagation?.();
                    if (e && typeof e.preventDefault === "function")
                      e.preventDefault();
                    if (e && typeof e.stopPropagation === "function")
                      e.stopPropagation();
                    e.defaultPrevented = true;
                    e.cancelBubble = true;
                    return;
                  }

                  if (imageObj.isOnTopLayer) {
                    // Top-layer image: merge into selection locally (same as rect/circle), don't delegate to onClick
                    // CRITICAL: Build current list from selectedObjects AND selectedObject so we don't lose first selection
                    // (e.g. user selected one top-layer image then enabled multi-select; selectedObjects may be empty)
                    const alreadyInSelection =
                      selectedObjects?.some(
                        (obj) => obj.id === imageObj.id && obj.type === "Image",
                      ) ||
                      (selectedObject?.id === imageObj.id &&
                        selectedObject?.type === "Image");
                    if (!alreadyInSelection) {
                      setSelectedObjects((prev) => {
                        const current =
                          prev && Array.isArray(prev) ? [...prev] : [];
                        const ids = new Set(current.map((o) => o?.id));
                        if (selectedObject?.id && !ids.has(selectedObject.id)) {
                          current.push({
                            ...selectedObject,
                            type: selectedObject.type || "Image",
                          });
                          ids.add(selectedObject.id);
                        }
                        if (!ids.has(imageObj.id)) current.push(newSelected);
                        return current;
                      });
                    }
                    setSelectedObject(newSelected);
                    setSelectedImage(latestImage);
                    setSelectedText(null);
                    setShowTextBox(false);
                    setSelectedArrow(null);
                    setSelectedDoubleSidedArrow(null);
                    setSelectedLine(null);
                  } else {
                    // Non-top-layer image: merge here too so first selection (e.g. top-layer image) is never lost
                    const alreadyInSelection =
                      selectedObjects?.some(
                        (obj) => obj.id === imageObj.id && obj.type === "Image",
                      ) ||
                      (selectedObject?.id === imageObj.id &&
                        selectedObject?.type === "Image");
                    if (!alreadyInSelection) {
                      setSelectedObjects((prev) => {
                        const current =
                          prev && Array.isArray(prev) ? [...prev] : [];
                        const ids = new Set(current.map((o) => o?.id));
                        if (selectedObject?.id && !ids.has(selectedObject.id)) {
                          current.push({
                            ...selectedObject,
                            type: selectedObject.type || "Image",
                          });
                          ids.add(selectedObject.id);
                        }
                        if (!ids.has(imageObj.id)) current.push(newSelected);
                        return current;
                      });
                    }
                    setSelectedObject(newSelected);
                    setSelectedImage(latestImage);
                    setSelectedText(null);
                    setShowTextBox(false);
                    setSelectedArrow(null);
                    setSelectedDoubleSidedArrow(null);
                    setSelectedLine(null);
                  }
                  // Prevent handleImageClick and Background from clearing
                  e.evt?.preventDefault?.();
                  e.evt?.stopPropagation?.();
                  if (e && typeof e.preventDefault === "function")
                    e.preventDefault();
                  if (e && typeof e.stopPropagation === "function")
                    e.stopPropagation();
                  e.defaultPrevented = true;
                  e.cancelBubble = true;
                } else {
                  if (isSameElement) {
                    return;
                  }
                  const hasDifferentSelection =
                    (!!selectedObject?.id && selectedObject.id !== imageObj.id) ||
                    (Array.isArray(selectedObjects) &&
                      selectedObjects.some((obj) => obj?.id && obj.id !== imageObj.id));
                  const shouldClearFirstForImage =
                    !isMultiSelectMode && hasDifferentSelection;
                  
                  if (shouldClearFirstForImage) {
                    setSelectedObject(null);
                    setSelectedObjects([]);
                    setShowTextBox(false);
                    setSelectedText(null);
                    setSelectedImage?.(null);
                    if (setSelectedArrow) setSelectedArrow(null);
                    if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
                    if (setSelectedLine) setSelectedLine(null);
                    e.evt?.preventDefault?.();
                    e.evt?.stopPropagation?.();
                    e.cancelBubble = true;
                    return;
                  }
                  // Normal mode: handle selection directly
                  setSelectedObject(newSelected);
                  setSelectedObjects([newSelected]);
                  // CRITICAL: Also set selectedImage from latestImage (images array) so ImageEditor
                  // shows persisted props (e.g. referenceUrl) when reselecting after unselect
                  if (setSelectedImage) {
                    setSelectedImage(newSelected);
                  }
                }
              }
            : undefined
        }
        onDragMove={
          isInteractive
            ? (newAttrs, event) => {
                // If multiple elements are selected AND this element is part of the selection,
                // use group drag handler instead of individual drag
                const isInSelectedObjects = selectedObjects?.some(
                  (obj) => obj.id === newAttrs.id && obj.type === "Image",
                );

                // CRITICAL FIX: Check if group drag was already initialized in onDragStart
                // If so, use group drag handler immediately without re-initializing
                if (
                  selectedObjects &&
                  selectedObjects.length > 1 &&
                  isInSelectedObjects
                ) {
                  // Use group drag handler - pass event for accurate boundary detection
                  handleGroupDragMove?.(
                    newAttrs.id,
                    "Image",
                    newAttrs.x,
                    newAttrs.y,
                    event,
                  );
                  return;
                }

                // Normal individual drag behavior
                const isTopLayerImage =
                  imageObj?.isOnTopLayer === true ||
                  newAttrs?.isOnTopLayer === true;

                // For non-top-layer images, use dragPreview ghost
                if (!isTopLayerImage) {
                  handleDragPreview?.({
                    event,
                    newAttrs,
                    sourceStageRef,
                    element: imageObj,
                    type: "Image",
                  });

                  // Simple coordinate conversion for editor updates
                  // CRITICAL FIX: Match rectangleRenderer logic - use targetCanvas based on mouse position
                  let editorAttrs = { ...newAttrs };
                  let targetCanvas = null;

                  if (
                    event?.evt &&
                    sourceStageRef &&
                    offCanvasStageRef?.current
                  ) {
                    const clientX = event.evt.clientX;
                    const clientY = event.evt.clientY;
                    const mainStage = stageRef?.current;
                    const offCanvasStage = offCanvasStageRef.current;
                    const mainBBox = mainStage
                      ?.container?.()
                      ?.getBoundingClientRect?.();
                    const offBBox = offCanvasStage
                      ?.container?.()
                      ?.getBoundingClientRect?.();

                    if (mainBBox && offBBox) {
                      const withinMain =
                        clientX >= mainBBox.left &&
                        clientX <= mainBBox.right &&
                        clientY >= mainBBox.top &&
                        clientY <= mainBBox.bottom;

                      const withinOff =
                        clientX >= offBBox.left &&
                        clientX <= offBBox.right &&
                        clientY >= offBBox.top &&
                        clientY <= offBBox.bottom;

                      // Determine target canvas based on mouse position (like rectangleRenderer)
                      targetCanvas = withinMain
                        ? "main"
                        : withinOff
                          ? "offCanvas"
                          : null;

                      // CRITICAL FIX: Match rectangleRenderer logic exactly
                      // If dragging over off-canvas area but element is on main canvas stage,
                      // convert the position to be relative to off-canvas stage origin
                      if (
                        targetCanvas === "offCanvas" &&
                        !isOffCanvas &&
                        mainBBox &&
                        offBBox
                      ) {
                        // Element is on main canvas stage but being dragged over off-canvas area
                        // Convert position from main canvas stage coordinates to off-canvas stage coordinates

                        // Step 1: Convert from main canvas stage coordinates to absolute/client coordinates
                        const absX = mainBBox.left + newAttrs.x;
                        const absY = mainBBox.top + newAttrs.y;

                        // Step 2: Convert from absolute/client coordinates to off-canvas stage coordinates
                        const convertedX = absX - offBBox.left;
                        const convertedY = absY - offBBox.top;

                        editorAttrs = {
                          ...newAttrs,
                          x: convertedX,
                          y: convertedY,
                          _isInOffCanvasDuringDrag: true,
                        };
                      }
                      // CRITICAL: If dragging over main canvas area but element is on off-canvas stage,
                      // convert the position to be relative to main canvas stage origin
                      else if (
                        targetCanvas === "main" &&
                        isOffCanvas &&
                        mainBBox &&
                        offBBox
                      ) {
                        // Element is on off-canvas stage but being dragged over main canvas area
                        // Convert position from off-canvas stage coordinates to main canvas stage coordinates

                        // Step 1: Convert from off-canvas stage coordinates to absolute/client coordinates
                        const absX = offBBox.left + newAttrs.x;
                        const absY = offBBox.top + newAttrs.y;

                        // Step 2: Convert from absolute/client coordinates to main canvas stage coordinates
                        const convertedX = absX - mainBBox.left;
                        const convertedY = absY - mainBBox.top;

                        editorAttrs = {
                          ...newAttrs,
                          x: convertedX,
                          y: convertedY,
                          _isInOffCanvasDuringDrag: false,
                        };
                      } else {
                        // No conversion needed - element and mouse are on same canvas
                        // But still use newAttrs for the position
                        editorAttrs = {
                          ...newAttrs,
                          _isInOffCanvasDuringDrag: isOffCanvas,
                        };
                      }
                    }
                  }

                  // Update selectedObject with converted coordinates for ImageEditor
                  // CRITICAL: Remove _isInOffCanvasDuringDrag flag to prevent findElementCanvas from hiding the element
                  // CRITICAL: Merge imageObj so referenceUrl and other editor-only props are preserved during drag
                  const sourceStage = isOffCanvas ? "offCanvas" : "main";
                  const previewTargetStage = targetCanvas ?? sourceStage;
                  const selectionBase =
                    previewTargetStage !== sourceStage ? newAttrs : editorAttrs;
                  const {
                    _isInOffCanvasDuringDrag,
                    ...editorAttrsForSelection
                  } = selectionBase;
                  const updatedSelection = {
                    ...imageObj,
                    ...editorAttrsForSelection,
                    type: "Image",
                    _isDragging: true,
                    _previewTargetStage: sourceStage,
                  };
                  const now = Date.now();
                  if (now - _lastImageDragStateTime >= DRAG_STATE_THROTTLE_MS) {
                    _lastImageDragStateTime = now;
                    setSelectedObject(updatedSelection);
                    if (setSelectedImage) setSelectedImage(updatedSelection);
                    setSelectedObjects((prev) => {
                      const existingIndex = prev.findIndex(
                        (obj) => obj.id === newAttrs.id && obj.type === "Image",
                      );
                      if (existingIndex >= 0) {
                        const updated = [...prev];
                        updated[existingIndex] = updatedSelection;
                        return updated;
                      }
                      return prev;
                    });
                  }
                } else {
                  // For top-layer images, simplified handling
                  // CRITICAL FIX: Match non-top-layer image behavior - use converted coordinates for editor
                  handleDragPreview?.({
                    event,
                    newAttrs,
                    sourceStageRef,
                    element: imageObj,
                    type: "Image",
                  });

                  // Simple coordinate conversion for top-layer images
                  // CRITICAL FIX: Match rectangleRenderer logic - use targetCanvas based on mouse position
                  let editorAttrs = { ...newAttrs };
                  let targetCanvas = null; // Initialize targetCanvas outside the if block

                  if (
                    event?.evt &&
                    sourceStageRef &&
                    offCanvasStageRef?.current
                  ) {
                    const clientX = event.evt.clientX;
                    const clientY = event.evt.clientY;
                    const mainStage = stageRef?.current;
                    const offCanvasStage = offCanvasStageRef.current;
                    const mainBBox = mainStage
                      ?.container?.()
                      ?.getBoundingClientRect?.();
                    const offBBox = offCanvasStage
                      ?.container?.()
                      ?.getBoundingClientRect?.();

                    if (mainBBox && offBBox) {
                      const withinMain =
                        clientX >= mainBBox.left &&
                        clientX <= mainBBox.right &&
                        clientY >= mainBBox.top &&
                        clientY <= mainBBox.bottom;

                      const withinOff =
                        clientX >= offBBox.left &&
                        clientX <= offBBox.right &&
                        clientY >= offBBox.top &&
                        clientY <= offBBox.bottom;

                      // Determine target canvas based on mouse position (like rectangleRenderer)
                      targetCanvas = withinMain
                        ? "main"
                        : withinOff
                          ? "offCanvas"
                          : null;

                      // CRITICAL FIX: Match rectangleRenderer logic exactly
                      // Top-layer images are always on main canvas (isOffCanvas = false)
                      // If dragging over off-canvas area but element is on main canvas stage,
                      // convert the position to be relative to off-canvas stage origin
                      if (
                        targetCanvas === "offCanvas" &&
                        !isOffCanvas &&
                        mainBBox &&
                        offBBox
                      ) {
                        // Element is on main canvas stage but being dragged over off-canvas area
                        // Convert position from main canvas stage coordinates to off-canvas stage coordinates

                        // Step 1: Convert from main canvas stage coordinates to absolute/client coordinates
                        const absX = mainBBox.left + newAttrs.x;
                        const absY = mainBBox.top + newAttrs.y;

                        // Step 2: Convert from absolute/client coordinates to off-canvas stage coordinates
                        const convertedX = absX - offBBox.left;
                        const convertedY = absY - offBBox.top;

                        editorAttrs = {
                          ...newAttrs,
                          x: convertedX,
                          y: convertedY,
                          _isInOffCanvasDuringDrag: true,
                        };
                      }
                      // CRITICAL: If dragging over main canvas area but element is on off-canvas stage,
                      // convert the position to be relative to main canvas stage origin
                      else if (
                        targetCanvas === "main" &&
                        isOffCanvas &&
                        mainBBox &&
                        offBBox
                      ) {
                        // Element is on off-canvas stage but being dragged over main canvas area
                        // Convert position from off-canvas stage coordinates to main canvas stage coordinates

                        // Step 1: Convert from off-canvas stage coordinates to absolute/client coordinates
                        const absX = offBBox.left + newAttrs.x;
                        const absY = offBBox.top + newAttrs.y;

                        // Step 2: Convert from absolute/client coordinates to main canvas stage coordinates
                        const convertedX = absX - mainBBox.left;
                        const convertedY = absY - mainBBox.top;

                        editorAttrs = {
                          ...newAttrs,
                          x: convertedX,
                          y: convertedY,
                          _isInOffCanvasDuringDrag: false,
                        };
                      } else {
                        // No conversion needed - element and mouse are on same canvas
                        // But still use newAttrs for the position
                        editorAttrs = {
                          ...newAttrs,
                          _isInOffCanvasDuringDrag: isOffCanvas,
                        };
                      }
                    }
                  }

                  // Update selectedObject with converted coordinates for ImageEditor
                  // CRITICAL: Remove _isInOffCanvasDuringDrag flag to prevent findElementCanvas from hiding the element
                  // CRITICAL: Merge imageObj so referenceUrl and other editor-only props are preserved during drag
                  const sourceStage = isOffCanvas ? "offCanvas" : "main";
                  const previewTargetStage = targetCanvas ?? sourceStage;
                  const selectionBase =
                    previewTargetStage !== sourceStage ? newAttrs : editorAttrs;
                  const {
                    _isInOffCanvasDuringDrag: _unused,
                    ...editorAttrsForSelection
                  } = selectionBase;
                  const updatedSelection = {
                    ...imageObj,
                    ...editorAttrsForSelection,
                    type: "Image",
                    _isDragging: true,
                    _previewTargetStage: sourceStage,
                  };
                  const now = Date.now();
                  if (now - _lastImageDragStateTime >= DRAG_STATE_THROTTLE_MS) {
                    _lastImageDragStateTime = now;
                    setSelectedObject(updatedSelection);
                    if (setSelectedImage) setSelectedImage(updatedSelection);
                    setSelectedObjects((prev) => {
                      const existingIndex = prev.findIndex(
                        (obj) => obj.id === newAttrs.id && obj.type === "Image",
                      );
                      if (existingIndex >= 0) {
                        const updated = [...prev];
                        updated[existingIndex] = updatedSelection;
                        return updated;
                      }
                      return prev;
                    });
                  }
                }
              }
            : undefined
        }
        onTransformStart={
          isInteractive
            ? () => {
                // CRITICAL: Skip for MAIN CANVAS top-layer images - they're handled by Canvas.jsx transformer
                // BUT off-canvas top-layer images use their own local transformer
                if (imageObj.isOnTopLayer && !isOffCanvas) {
                  return;
                }

                // Always refresh initial transform state for each transform gesture.
                // Keeping a stale "original" state across gestures causes incorrect
                // change detection and makes transform diagnostics misleading.
                const initialImage = (isOffCanvas ? offCanvasImages : images).find(
                  (img) => img.id === imageObj.id,
                );
                if (initialImage) {
                  isTransformingRef.current.initialTransformState = {
                    id: initialImage.id,
                    rotation: initialImage.rotation || 0,
                    scaleX: initialImage.scaleX || 1,
                    scaleY: initialImage.scaleY || 1,
                    width: initialImage.width || 0,
                    height: initialImage.height || 0,
                    x: initialImage.x || 0,
                    y: initialImage.y || 0,
                    isOffCanvas,
                    isOnTopLayer: !!imageObj.isOnTopLayer,
                  };
                }

                isTransformingRef.current.isTransforming = true;
                // Set ref so stage click that may fire on release (before onTransformEnd) does not clear selection
                if (justFinishedTransformRef) {
                  justFinishedTransformRef.current = true;
                }
                if (lastTransformStartTimeRef) {
                  lastTransformStartTimeRef.current = Date.now();
                }
                if (selectionBoxRef) selectionBoxRef.current = null;
                if (selectionBoxStageRef) selectionBoxStageRef.current = null;
              }
            : undefined
        }
        onTransform={
          isInteractive
            ? (newAttrs) => {
                // CRITICAL: Skip for MAIN CANVAS top-layer images - they're handled by Canvas.jsx transformer
                // BUT off-canvas top-layer images use their own local transformer
                if (imageObj.isOnTopLayer && !isOffCanvas) {
                  return;
                }

                // CRITICAL FIX: For off-canvas top-layer images, DON'T update state during transform
                // Updating selectedObject causes re-renders which interfere with smooth rotation
                // The transformer handles the visual transform, and we'll update state in onTransformEnd
                // This matches how main canvas top-layer images work
                if (imageObj.isOnTopLayer && isOffCanvas) {
                  // Don't update state during transform for off-canvas top-layer images
                  // This prevents fluctuation and position jumps during rotation/resize
                  return;
                }

                // For non-top-layer images, update selected object AND selectedImage for live editor feedback.
                // CRITICAL: Do NOT call updateImageProperties or write to images arrays here; that still happens
                // in onTransformEnd so history entries stay grouped, just like rectangles/circles.
                const sourceArray = isOffCanvas ? offCanvasImages : images;
                const baseImage =
                  sourceArray?.find((img) => img.id === newAttrs.id) ||
                  imageObj;

                const updatedSelection = {
                  ...baseImage,
                  ...newAttrs,
                  type: "Image",
                };
                if (
                  isMainNonTopTransformDebugEnabled &&
                  !isOffCanvas &&
                  !imageObj.isOnTopLayer
                ) {
                  console.log("[imageRenderer main-non-top] onTransform", {
                    id: newAttrs.id,
                    x: newAttrs.x,
                    y: newAttrs.y,
                    width: newAttrs.width,
                    height: newAttrs.height,
                    rotation: newAttrs.rotation,
                    scaleX: newAttrs.scaleX,
                    scaleY: newAttrs.scaleY,
                  });
                }

                const now = Date.now();
                if (now - _lastImageDragStateTime >= DRAG_STATE_THROTTLE_MS) {
                  _lastImageDragStateTime = now;
                  setSelectedObject(updatedSelection);
                  if (setSelectedImage) setSelectedImage(updatedSelection);
                  setSelectedObjects([{ ...updatedSelection }]);
                }
              }
            : undefined
        }
        onTransformEnd={(newAttrs) => {
          if (!isInteractive) {
            return;
          }
          if (justFinishedTransformRef) {
            justFinishedTransformRef.current = true;
          }

          // CRITICAL: Skip for MAIN CANVAS top-layer images - they're handled by Canvas.jsx transformer
          // Top-layer images on main canvas use the topLayerTransformerRef in Canvas.jsx
          // BUT off-canvas top-layer images use their own local transformer (ImageElement.jsx)
          if (imageObj.isOnTopLayer && !isOffCanvas) {
            return;
          }

          isTransformingRef.current.isTransforming = false;

          const stageNode = isOffCanvas
            ? offCanvasStageRef?.current
            : stageRef?.current;
          const stageWidth =
            Number(stageNode?.width?.()) ||
            (isOffCanvas ? window.innerWidth : window.innerWidth * 0.45);
          const stageHeight =
            Number(stageNode?.height?.()) || window.innerHeight;
          const edgeInset = 10;
          const targetImage =
            (isOffCanvas ? offCanvasImages : images).find(
              (img) => img.id === newAttrs.id,
            ) || imageObj;
          const safeWidth = Math.max(0, Number(newAttrs.width) || 0);
          const safeHeight = Math.max(0, Number(newAttrs.height) || 0);
          const halfWidth = safeWidth / 2;
          const halfHeight = safeHeight / 2;
          const rotationDeg = Number(newAttrs.rotation) || 0;
          const theta = (rotationDeg * Math.PI) / 180;
          const absCos = Math.abs(Math.cos(theta));
          const absSin = Math.abs(Math.sin(theta));
          const rotatedHalfWidth = halfWidth * absCos + halfHeight * absSin;
          const rotatedHalfHeight = halfWidth * absSin + halfHeight * absCos;
          const usableStageWidth = isOffCanvas
            ? getOffCanvasMainColumnBounds(stageWidth).mainZoneStart
            : stageWidth;
          const minCenterX = rotatedHalfWidth + edgeInset;
          const maxCenterX = Math.max(
            minCenterX,
            usableStageWidth - rotatedHalfWidth - edgeInset,
          );
          const minCenterY = rotatedHalfHeight + edgeInset;
          const maxCenterY = Math.max(
            minCenterY,
            stageHeight - rotatedHalfHeight - edgeInset,
          );
          const useTopLeftAnchor = targetImage?.useTopLeftAnchor === true;
          const candidateX = Number(newAttrs.x) || 0;
          const candidateY = Number(newAttrs.y) || 0;
          let clampedX = candidateX;
          let clampedY = candidateY;
          if (useTopLeftAnchor) {
            const relX = -halfWidth;
            const relY = -halfHeight;
            const offsetX = relX * Math.cos(theta) - relY * Math.sin(theta);
            const offsetY = relX * Math.sin(theta) + relY * Math.cos(theta);
            const centerX = candidateX - offsetX;
            const centerY = candidateY - offsetY;
            const clampedCenterX = Math.max(
              minCenterX,
              Math.min(maxCenterX, centerX),
            );
            const clampedCenterY = Math.max(
              minCenterY,
              Math.min(maxCenterY, centerY),
            );
            clampedX = clampedCenterX + offsetX;
            clampedY = clampedCenterY + offsetY;
          } else {
            clampedX = Math.max(minCenterX, Math.min(maxCenterX, candidateX));
            clampedY = Math.max(minCenterY, Math.min(maxCenterY, candidateY));
          }
          const adjustedNewAttrs = {
            ...newAttrs,
            x: clampedX,
            y: clampedY,
          };
          if (
            isMainNonTopTransformDebugEnabled &&
            !isOffCanvas &&
            !imageObj.isOnTopLayer
          ) {
            console.log("[imageRenderer main-non-top] onTransformEnd", {
              id: adjustedNewAttrs.id,
              x: adjustedNewAttrs.x,
              y: adjustedNewAttrs.y,
              width: adjustedNewAttrs.width,
              height: adjustedNewAttrs.height,
              rotation: adjustedNewAttrs.rotation,
              scaleX: adjustedNewAttrs.scaleX,
              scaleY: adjustedNewAttrs.scaleY,
            });
          }

          // CRITICAL: Check for actual changes against INITIAL state (from onTransformStart)
          // Not against current state, which may have been updated by previous onTransformEnd calls
          const initialState = isTransformingRef.current.initialTransformState;
          const hasActualChanges =
            initialState &&
            (Math.abs((initialState.rotation || 0) - (adjustedNewAttrs.rotation || 0)) >
              0.01 ||
              Math.abs((initialState.scaleX || 1) - (newAttrs.scaleX || 1)) >
                0.01 ||
              Math.abs((initialState.scaleY || 1) - (newAttrs.scaleY || 1)) >
                0.01 ||
              Math.abs((initialState.width || 0) - (newAttrs.width || 0)) >
                0.01 ||
              Math.abs((initialState.height || 0) - (adjustedNewAttrs.height || 0)) >
                0.01);

          // CRITICAL: DON'T clear initial state here - keep it for the entire editing session
          // This ensures all transforms in the session are compared against the ORIGINAL state
          // The initial state will be cleared when the element is unselected (in endElementEditing)
          // if (isTransformingRef.initialTransformState) {
          //   delete isTransformingRef.initialTransformState;
          // }

          // Calculate the updated state BEFORE calling setState
          // This ensures saveToHistory receives the correct updated state
          // CRITICAL: Ensure rotation, scaleX, scaleY are explicitly preserved from newAttrs
          // CRITICAL FIX: Preserve isTransform flag in updatedImages so Canvas.jsx useEffect can skip sync
          const updatedImages = isOffCanvas
            ? offCanvasImages.map((img) => {
                if (img.id === newAttrs.id) {
                  // Explicitly preserve rotation, scaleX, scaleY from newAttrs
                  return {
                    ...img,
                    ...adjustedNewAttrs,
                    // CRITICAL: Explicitly set rotation, scaleX, scaleY to ensure they override any undefined values
                    rotation: adjustedNewAttrs.rotation ?? img.rotation ?? 0,
                    scaleX: adjustedNewAttrs.scaleX ?? img.scaleX ?? 1,
                    scaleY: adjustedNewAttrs.scaleY ?? img.scaleY ?? 1,
                    // Preserve isTransform flag if present
                    isTransform: adjustedNewAttrs.isTransform,
                  };
                }
                return img;
              })
            : images.map((img) => {
                if (img.id === newAttrs.id) {
                  // Explicitly preserve rotation, scaleX, scaleY from newAttrs
                  return {
                    ...img,
                    ...adjustedNewAttrs,
                    // CRITICAL: Explicitly set rotation, scaleX, scaleY to ensure they override any undefined values
                    rotation: adjustedNewAttrs.rotation ?? img.rotation ?? 0,
                    scaleX: adjustedNewAttrs.scaleX ?? img.scaleX ?? 1,
                    scaleY: adjustedNewAttrs.scaleY ?? img.scaleY ?? 1,
                    // Preserve isTransform flag if present
                    isTransform: adjustedNewAttrs.isTransform,
                  };
                }
                return img;
              });

          // Transform end - save rotation to history
          const updatedImage = updatedImages.find(
            (img) => img.id === newAttrs.id,
          );
          if (updatedImage) {
            // Rotation and transform properties preserved
          }

          // CRITICAL FIX: Use the updated image from updatedImages array to ensure rotation is preserved
          // This ensures the state update includes all transform properties (rotation, scaleX, scaleY)
          const updatedImageForState = updatedImages.find(
            (img) => img.id === newAttrs.id,
          );

          // CRITICAL FIX: Clear isTransform flag after spreading to prevent useEffect from skipping sync
          const cleanAttrs = updatedImageForState
            ? { ...updatedImageForState }
            : { ...newAttrs };
          delete cleanAttrs.isTransform;

          // CRITICAL: Use updatedImageForState to ensure rotation and all transform properties are preserved
          setImageState((prev) =>
            prev.map((img) =>
              img.id === newAttrs.id
                ? updatedImageForState
                  ? { ...updatedImageForState, isTransform: undefined }
                  : { ...img, ...cleanAttrs }
                : img,
            ),
          );
          setSelectedObject({ ...cleanAttrs, type: "Image" });
          if (updateImageProperties) {
            // Skip history save here - we'll save it below with the updated state
            // The renderer's onTransformEnd will handle the history save with the correct updated state
            // The wrapped function signature is: (id, newProps, isDragging = false, skipHistorySave = false)
            updateImageProperties(adjustedNewAttrs.id, cleanAttrs, false, true); // isDragging=false, skipHistorySave=true
          }
          setSelectedObjects([{ ...cleanAttrs, type: "Image" }]);

          // CRITICAL: Store the updated images array in ref so element_edit_complete can use it
          // This ensures rotation and other transform properties are saved to history
          // Only store if element is selected/being edited AND there are actual changes
          if (
            (isSelected || isElementBeingEdited) &&
            latestUpdatedImagesRef &&
            hasActualChanges
          ) {
            // Store the updated images array (with rotation) in the ref
            // Clear isTransform flag from all images before storing
            const cleanedUpdatedImages = updatedImages.map((img) => {
              const { isTransform, ...cleanedImg } = img;
              return cleanedImg;
            });
            // Store in the appropriate canvas (main or off-canvas)
            if (isOffCanvas) {
              latestUpdatedImagesRef.current.offCanvas = cleanedUpdatedImages;
            } else {
              latestUpdatedImagesRef.current.main = cleanedUpdatedImages;
            }

            // Set pendingChanges since there are actual changes
            setPendingChanges(true);
            // CRITICAL: Also update the ref to ensure endElementEditing sees the change
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          } else if ((isSelected || isElementBeingEdited) && hasActualChanges) {
            // Set pendingChanges since there are actual changes
            setPendingChanges(true);
            // CRITICAL: Also update the ref to ensure endElementEditing sees the change
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          } else if (!hasActualChanges) {
          }

          // CRITICAL: Don't save to history here - history will be saved when element is unselected
          // This groups all transform changes (width, height, rotation, etc.) into a single history entry
        }}
        onDragEnd={
          isInteractive
            ? (id, newX, newY, sourceStageRef, mouseClientX, mouseClientY) => {
                const selectedOnlyThisImageInMultiSelect =
                  isMultiSelectMode &&
                  selectedObject?.id === id &&
                  selectedObject?.type === "Image" &&
                  (selectedObjects?.length || 0) <= 1;
                if (selectedOnlyThisImageInMultiSelect) {
                  const baseImage = (isOffCanvas ? offCanvasImages : images).find(
                    (img) => img.id === id,
                  );
                  const dx = Math.abs((Number(newX) || 0) - (Number(baseImage?.x) || 0));
                  const dy = Math.abs((Number(newY) || 0) - (Number(baseImage?.y) || 0));
                  const looksLikeClickNotDrag = dx <= 2 && dy <= 2;
                  if (looksLikeClickNotDrag) {
                    clearDragPreview?.();
                    return;
                  }
                }
                // CRITICAL: Set first so the click that may fire after drag end does not clear selection
                if (justFinishedDragRef) {
                  justFinishedDragRef.current = true;
                  setTimeout(() => {
                    if (justFinishedDragRef)
                      justFinishedDragRef.current = false;
                  }, 1200);
                }
                if (lastDraggedImageRef) {
                  lastDraggedImageRef.current = { id, time: Date.now() };
                  setTimeout(() => {
                    if (lastDraggedImageRef)
                      lastDraggedImageRef.current = { id: null, time: 0 };
                  }, 1200);
                }

                clearDragPreview?.();

                // CRITICAL: Get the current image properties from the Konva node, not from stale state
                let currentImage = null;
                const stage = sourceStageRef?.current || sourceStageRef; // Handle both ref and direct stage
                if (stage) {
                  const node = stage.findOne(`#${id}`);
                  if (node) {
                    // Get the base image from state to preserve all other properties
                    const baseImage = (
                      isOffCanvas ? offCanvasImages : images
                    ).find((img) => img.id === id);

                    if (baseImage) {
                      // Get current rotation from the node
                      let nodeRotation = node.rotation();
                      nodeRotation = nodeRotation % 360;
                      if (nodeRotation < 0) nodeRotation += 360;

                      // CRITICAL: For cropped images, we need to extract just the post-crop rotation
                      const isCroppedImage = baseImage.cropData;
                      const isApiImage =
                        baseImage.src &&
                        (baseImage.src.startsWith("http://") ||
                          baseImage.src.startsWith("https://"));
                      const hasBakedTransforms =
                        baseImage.hasBakedTransforms ?? false;

                      let actualRotation = nodeRotation;

                      // For cropped images that aren't baked and aren't API images,
                      // the node rotation includes both originalCropRotation and rotation
                      // We need to extract just the rotation part
                      if (
                        isCroppedImage &&
                        !hasBakedTransforms &&
                        !isApiImage
                      ) {
                        const originalCropRotation =
                          baseImage.originalCropRotation || 0;
                        // Node rotation = originalCropRotation + rotation
                        // So: rotation = nodeRotation - originalCropRotation
                        actualRotation =
                          (nodeRotation - originalCropRotation) % 360;
                        if (actualRotation < 0) actualRotation += 360;
                      }
                      // For all other cases (non-cropped, baked, or API images), use node rotation directly

                      // Create currentImage with properties from node (rotation, scaleX, scaleY, position)
                      // CRITICAL: Set rotation, scaleX, scaleY AFTER spreading baseImage to ensure they override any stale values
                      currentImage = {
                        ...baseImage,
                        x: newX,
                        y: newY,
                        // CRITICAL: These must come AFTER the spread to override any stale values from baseImage
                        rotation: actualRotation, // Use the calculated rotation from node
                        scaleX: node.scaleX(),
                        scaleY: node.scaleY(),
                      };

                      // Rotation preserved during drag
                    }
                  }
                }

                // Fallback: if we couldn't get the node, use the image from state
                if (!currentImage) {
                  currentImage = (isOffCanvas ? offCanvasImages : images).find(
                    (img) => img.id === id,
                  );
                  if (currentImage) {
                    // At least preserve the position update and all other properties
                    currentImage = {
                      ...currentImage,
                      x: newX,
                      y: newY,
                    };
                  }
                }

                // CRITICAL: Don't update state here - let handleImageDragEnd handle all state updates
                // Pass the current image to handleElementDragEnd so it can preserve all properties
                handleElementDragEnd({
                  id,
                  newX,
                  newY,
                  type: "Image",
                  sourceStageRef,
                  mouseClientX,
                  mouseClientY,
                  mainState: images,
                  offCanvasState: offCanvasImages,
                  setMainState: setImages,
                  setOffCanvasState: setOffCanvasImages,
                  setSelectedObjects,
                  currentImage, // Pass current image with properties from Konva node
                  setSelectedObject, // CRITICAL: Pass setSelectedObject so dragEndUtils can update it with final coordinates
                  setSelectedImage, // CRITICAL: Pass setSelectedImage so ImageEditor updates with final coordinates
                });

                // Note: setSelectedObject and setSelectedImage are now handled by handleElementDragEnd with final coordinates
                // No need to update them here with potentially incorrect coordinates
              }
            : undefined
        }
        handleImageClick={(id, e) => {
          // CRITICAL: Don't select images in clone mode - keep original selected
          if (isCloneMode || lockedAction) {
            return;
          }
          const latestImage = (isOffCanvas ? offCanvasImages : images).find(
            (img) => img.id === id,
          );
          if (!latestImage) return;

          const imageWithType = { ...latestImage, type: "Image" };
          const clickedImageIsMainCanvas = !isOffCanvas;
          const hasDifferentSelectedObject =
            !!selectedObject?.id && selectedObject.id !== id;
          const hasDifferentSelectedInArray =
            Array.isArray(selectedObjects) &&
            selectedObjects.some((obj) => obj?.id && obj.id !== id);
          const shouldClearFirstForImageClickHandler =
            !isMultiSelectMode &&
            (hasDifferentSelectedObject || hasDifferentSelectedInArray);
          
          const isSingleSelectedImageToggleOff =
            isMultiSelectMode &&
            selectedObject?.id === id &&
            selectedObject?.type === "Image" &&
            (selectedObjects?.length || 0) <= 1;
          if (isSingleSelectedImageToggleOff) {
            cancelActiveGroupDrag?.();
            setSelectedObject(null);
            setSelectedObjects([]);
            setShowTextBox(false);
            setSelectedText(null);
            setSelectedImage(null);
            if (setSelectedArrow) setSelectedArrow(null);
            if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
            if (setSelectedLine) setSelectedLine(null);
            e?.evt?.stopPropagation?.();
            e?.evt?.preventDefault?.();
            if (e) e.cancelBubble = true;
            return;
          }
          if (shouldClearFirstForImageClickHandler) {
            setSelectedObject(null);
            setSelectedObjects([]);
            setShowTextBox(false);
            setSelectedText(null);
            setSelectedImage(null);
            if (setSelectedArrow) setSelectedArrow(null);
            if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
            if (setSelectedLine) setSelectedLine(null);
            e?.evt?.stopPropagation?.();
            e?.evt?.preventDefault?.();
            if (e) e.cancelBubble = true;
            return;
          }

          // CRITICAL: Check for cross-canvas selection in multi-select mode (include selectedObject when selectedObjects is empty)
          if (
            isMultiSelectMode &&
            (selectedObjects?.length > 0 || selectedObject)
          ) {
            // Build list of all selected elements (including selectedObject if it exists)
            const allSelectedElements = [...(selectedObjects || [])];
            if (
              selectedObject &&
              !allSelectedElements.some(
                (obj) =>
                  obj.id === selectedObject.id &&
                  obj.type === selectedObject.type,
              )
            ) {
              allSelectedElements.push(selectedObject);
            }

            // Check if all selected elements are from the same canvas type
            const selectedElementsCanvasTypes = allSelectedElements.map((obj) =>
              isElementInMainCanvas(obj.id, obj.type),
            );
            const allFromMainCanvas = selectedElementsCanvasTypes.every(
              (isMain) => isMain === true,
            );
            const allFromOffCanvas = selectedElementsCanvasTypes.every(
              (isMain) => isMain === false,
            );

            // Prevent cross-canvas selection in multi-select mode
            if (
              (allFromMainCanvas && !clickedImageIsMainCanvas) ||
              (allFromOffCanvas && clickedImageIsMainCanvas)
            ) {
              e?.evt?.stopPropagation?.();
              e?.evt?.preventDefault?.();
              return;
            }
          }

          // CRITICAL: Handle multi-select mode with shift key
          if (isMultiSelectMode && e?.evt?.shiftKey) {
            // Shift+click in multi-select mode: add/remove from selectedObjects
            const isAlreadySelected = selectedObjects?.some(
              (obj) => obj.id === id && obj.type === "Image",
            );

            if (isAlreadySelected) {
              cancelActiveGroupDrag?.();
              // Remove from selection
              setSelectedObjects(
                selectedObjects.filter(
                  (obj) => !(obj.id === id && obj.type === "Image"),
                ),
              );
              let n = e?.target;
              while (n) {
                if (typeof n.stopDrag === "function") {
                  try {
                    n.stopDrag();
                  } catch (_) {
                    /* ignore */
                  }
                  break;
                }
                n = n.getParent?.() ?? null;
              }
            } else {
              // Add to selection
              setSelectedObjects([...selectedObjects, imageWithType]);
            }
          } else if (isMultiSelectMode) {
            // Click without shift in multi-select mode: just add to selection
            const isAlreadySelected = selectedObjects?.some(
              (obj) => obj.id === id && obj.type === "Image",
            );
            if (!isAlreadySelected) {
              setSelectedObjects([...selectedObjects, imageWithType]);
            }
          } else {
            // Normal mode: single selection
            setSelectedObject(imageWithType);
            setSelectedObjects([]);
          }
        }}
        isOffCanvas={isOffCanvas}
        isPreview={isPreview}
        isSelected={isSelected}
        isCloneMode={isCloneMode}
        lockedAction={lockedAction}
        isDuplicatePlacementActive={isDuplicatePlacementActive}
        renderOcclusionBlocker={
          // Off-canvas top-layer images use a local transformer and resizing can
          // visibly reveal the blocker as a duplicate image around the node.
          // Keep blocker for main top-layer and explicit forced cases only.
          (imageObj.isOnTopLayer === true && !isOffCanvas) ||
          imageObj._forceOcclusionBlocker === true
        }
        recentDraggableElementMousedownRef={recentDraggableElementMousedownRef}
        onCopyToOppositeCanvas={
          isInteractive && handleElementCopy && isOffCanvas
            ? () => handleElementCopy(imageObj.id, "Image", "offCanvas")
            : undefined
        }
      />
    );
  };
};
